import React from "react"
import { cn } from "@/components/common"

interface ProductPlaceholderProps {
  size?: "sm" | "md" | "lg"
  className?: string
}

/**
 * Beautiful placeholder for products without images
 * Features playful design elements fitting the kids toys/books theme
 */
const ProductPlaceholder: React.FC<ProductPlaceholderProps> = ({
  size = "md",
  className,
}) => {
  const iconSizes = {
    sm: "w-12 h-12",
    md: "w-16 h-16",
    lg: "w-24 h-24",
  }

  return (
    <div
      className={cn(
        "w-full h-full flex flex-col items-center justify-center bg-linear-to-br from-primary/5 via-background to-accent/10 relative overflow-hidden",
        className
      )}
    >
      {/* Decorative circles */}
      <div className="absolute top-4 left-4 w-8 h-8 bg-primary/10 rounded-full" />
      <div className="absolute bottom-6 right-6 w-12 h-12 bg-accent/20 rounded-full" />
      <div className="absolute top-1/4 right-8 w-6 h-6 bg-info/15 rounded-full" />
      <div className="absolute bottom-1/3 left-8 w-4 h-4 bg-success/20 rounded-full" />

      {/* Main icon - playful toy/book icon */}
      <div className={cn("text-primary/40 relative z-10", iconSizes[size])}>
        <svg
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
        >
          {/* Open book / toy box shape */}
          <rect
            x="8"
            y="16"
            width="48"
            height="36"
            rx="4"
            className="fill-primary/10 stroke-primary/30"
            strokeWidth="2"
          />
          {/* Book spine / divider */}
          <line
            x1="32"
            y1="16"
            x2="32"
            y2="52"
            className="stroke-primary/20"
            strokeWidth="2"
          />
          {/* Playful star */}
          <path
            d="M20 28L21.5 31.5L25 32L22 34.5L23 38L20 36L17 38L18 34.5L15 32L18.5 31.5L20 28Z"
            className="fill-warning/50"
          />
          {/* Small circle - like a ball/toy */}
          <circle cx="44" cy="32" r="4" className="fill-info/40" />
          {/* Heart shape */}
          <path
            d="M44 40C44 40 40 38 40 35.5C40 33.5 41.5 32 43 32C43.8 32 44 32.5 44 32.5C44 32.5 44.2 32 45 32C46.5 32 48 33.5 48 35.5C48 38 44 40 44 40Z"
            className="fill-destructive/30"
          />
        </svg>
      </div>

      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03] bg-dot-pattern" />
    </div>
  )
}

export default ProductPlaceholder
